/**
 * 地区筛选器中间件工具
 * value 类型：string[]
 */
import { isEmpty, sanitizeValues } from '@intelligence/v3/utils/common';
import {
  getDefaultApiFieldMap,
  getDefaultUrlFieldMap,
  getItemApiParamsFieldMap,
  getItemUrlParamsFieldMap,
} from '../shared/fieldMapUtils';
import { getNormalizedRegionSelectMarkets } from './utils';

import type { IFilterMiddlewareUtils } from '../shared/types';
import type { IFilterItemConfig } from '@intelligence/v3/filterCore/types';
import type { IRegionSelectMiddlewareParams } from './types';

const getApiParamsField = (item: IFilterItemConfig, params?: unknown): string => {
  const apiParamsFieldMap = getItemApiParamsFieldMap(item, params);
  const defaultApiFieldMap = getDefaultApiFieldMap(params);

  return (
    apiParamsFieldMap.markets ||
    apiParamsFieldMap.value ||
    defaultApiFieldMap.markets ||
    defaultApiFieldMap.value ||
    'markets'
  );
};

const getUrlParamsField = (item: IFilterItemConfig, params?: unknown): string => {
  const urlParamsFieldMap = getItemUrlParamsFieldMap(item, params);
  const defaultUrlFieldMap = getDefaultUrlFieldMap(params);

  return urlParamsFieldMap.markets || defaultUrlFieldMap.markets || defaultUrlFieldMap.value || 'markets';
};

type RegionSelectApiParams = IRecord<string[]>;

class RegionSelectMiddlewareUtils
implements IFilterMiddlewareUtils<string[], RegionSelectApiParams, IRegionSelectMiddlewareParams>
{
  convertToApiParams(
    item: IFilterItemConfig,
    filterValue?: string[],
    params?: IRegionSelectMiddlewareParams,
  ): RegionSelectApiParams | null {
    if (isEmpty(filterValue)) {
      return null;
    }

    const unique = getNormalizedRegionSelectMarkets(sanitizeValues(filterValue), params?.options);

    if (isEmpty(unique)) {
      return null;
    }

    const field = getApiParamsField(item, params);

    if (isEmpty(field)) {
      return null;
    }

    return {
      [field]: unique,
    };
  }

  getManagedUrlKeys(item: IFilterItemConfig, params?: IRegionSelectMiddlewareParams): string[] {
    const field = getUrlParamsField(item, params);

    return isEmpty(field) ? [] : [field];
  }

  deserializeUrlParams(
    item: IFilterItemConfig,
    searchParams: URLSearchParams,
    params?: IRegionSelectMiddlewareParams,
  ): string[] | null {
    if (isEmpty(searchParams)) {
      return null;
    }

    const field = getUrlParamsField(item, params);
    const rawValue = searchParams.get(field) || '';

    if (isEmpty(rawValue)) {
      return null;
    }

    const listValue = sanitizeValues(rawValue.split(',').map((itemValue) => itemValue.trim()));

    if (isEmpty(listValue)) {
      return null;
    }

    const normalizedValue = getNormalizedRegionSelectMarkets(listValue, params?.options);

    return isEmpty(normalizedValue) ? null : normalizedValue;
  }

  serializeUrlParams(
    item: IFilterItemConfig,
    filterValue?: string[],
    params?: IRegionSelectMiddlewareParams,
  ): IRecord<string> {
    if (isEmpty(filterValue)) {
      return {};
    }

    const unique = getNormalizedRegionSelectMarkets(sanitizeValues(filterValue), params?.options);

    if (isEmpty(unique)) {
      return {};
    }

    const field = getUrlParamsField(item, params);

    return { [field]: unique.join(',') };
  }
}

export default RegionSelectMiddlewareUtils;
